import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { API } from '../App';
import { toast } from 'sonner';
import { useCredits } from '../context/CreditContext';
import { useExitGuard, confirmExit } from '../hooks/useExitGuard';

const AVATAR_COLORS = ['#3B82F6','#8B5CF6','#EC4899','#F59E0B','#10B981','#EF4444','#06B6D4','#F97316'];
const RANK_COLORS = ['#854F0B','#5F5E5A','#993C1D'];
const ANIMALS = ['Fox','Owl','Bear','Wolf','Eagle','Panda','Tiger','Hawk','Lynx','Otter','Raven','Crane','Gecko','Bison','Koala'];

const firstNameOf = (full) => {
  if (!full) return '';
  const parts = String(full).trim().split(/\s+/);
  return parts[0] || '';
};

export default function QuickQuizPlayer({ quiz, onClose, isDark }) {
  const { user, isAuthenticated } = useCredits();
  // Logged-in users skip the name prompt entirely — we use their first name
  // and start the countdown immediately. Guests still go through the name step.
  const initialName = isAuthenticated ? firstNameOf(user?.name) : '';
  const initialPhase = isAuthenticated && initialName ? 'countdown' : 'name';
  const [playerName, setPlayerName] = useState(initialName);
  const [phase, setPhase] = useState(initialPhase); // name | checking | countdown | playing | submitting | results

  // Block accidental close while a quick quiz is in flight. The user already
  // chose a name and the timer is running — exiting would silently drop their
  // score, so we confirm first.
  const inPlay = phase === 'playing' || phase === 'countdown' || phase === 'submitting';
  useExitGuard(inPlay, 'Quit this quick quiz now? Your answers will be lost.');
  // Wrap the close button so the X also goes through confirmExit while playing.
  const handleClose = (result) => {
    if (!confirmExit(inPlay, 'Quit this quick quiz now? Your answers will be lost.')) return;
    onClose(result);
  };
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [timer, setTimer] = useState(0);
  const [countdownNum, setCountdownNum] = useState(3);
  const [selectedAnim, setSelectedAnim] = useState(null);
  const [showCorrect, setShowCorrect] = useState(false);
  const timerRef = useRef(null);

  const textMain = isDark ? 'text-white' : 'text-[#0e1f35]';
  const textMuted = isDark ? 'text-gray-400' : 'text-[#4a6078]';

  const questions = quiz?.questions || [];
  const settings = quiz?.settings || {};
  const timePerQ = settings.time_per_q || 60;
  const showAnswersMode = settings.show_answers || 'end';

  // Countdown before quiz starts
  useEffect(() => {
    if (phase !== 'countdown') return;
    if (countdownNum <= 0) { setPhase('playing'); setTimer(timePerQ); return; }
    const t = setTimeout(() => setCountdownNum(c => c - 1), 800);
    return () => clearTimeout(t);
  }, [phase, countdownNum, timePerQ]);

  // Per-question timer
  useEffect(() => {
    if (phase !== 'playing' || timePerQ <= 0) return;
    if (timer <= 0) { handleNext(true); return; }
    timerRef.current = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [phase, timer]); // eslint-disable-line

  const handleStart = async () => {
    if (!playerName.trim()) { toast.error('Enter your name'); return; }
    setPhase('checking');
    try {
      // Check if name is already used for this quiz
      const res = await axios.get(`${API}/quizzes/${quiz.slug}/check-name?name=${encodeURIComponent(playerName.trim())}`);
      if (res.data.taken) {
        const animal = ANIMALS[Math.floor(Math.random() * ANIMALS.length)];
        const padded = `${playerName.trim()} the ${animal}`;
        setPlayerName(padded);
        toast(`Name taken — you're now "${padded}"!`);
      }
    } catch {
      // If endpoint doesn't exist yet, just continue
    }
    setPhase('countdown');
    setCountdownNum(3);
  };

  const selectAnswer = useCallback((optIdx) => {
    if (selectedAnim !== null) return;
    const q = questions[currentQ];
    setAnswers(a => ({ ...a, [q.question_id]: optIdx }));
    setSelectedAnim(optIdx);

    if (showAnswersMode === 'each') {
      setShowCorrect(true);
      setTimeout(() => { setShowCorrect(false); setSelectedAnim(null); handleNext(false); }, 1200);
    } else {
      setTimeout(() => { setSelectedAnim(null); handleNext(false); }, 400);
    }
  }, [currentQ, questions, selectedAnim, showAnswersMode]); // eslint-disable-line

  const handleNext = useCallback((timeout) => {
    if (currentQ < questions.length - 1) {
      setCurrentQ(c => c + 1);
      setTimer(timePerQ);
    } else {
      handleSubmit();
    }
  }, [currentQ, questions.length, timePerQ]); // eslint-disable-line

  const handleSubmit = async () => {
    setPhase('submitting');
    clearTimeout(timerRef.current);
    try {
      // For authenticated users, the backend derives player_name from the
      // session and ignores any value sent by the client — omit it so the
      // intent is explicit and the wire payload stays clean.
      const payload = isAuthenticated
        ? { answers }
        : { player_name: playerName, answers };
      const res = await axios.post(
        `${API}/quizzes/${quiz.slug}/submit-guest`,
        payload,
        { withCredentials: true },
      );
      setResult(res.data);
      setPhase('results');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
      setPhase('playing');
    }
  };

  const timerPct = timePerQ > 0 ? (timer / timePerQ) * 100 : 100;
  const timerColor = timer <= 5 ? '#EF4444' : timer <= 10 ? '#F59E0B' : '#0d4a8a';

  // Full screen overlay
  return (
    <div className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center" data-testid="quiz-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={phase === 'name' ? () => handleClose(null) : undefined} />

      {/* Modal — on phones we go edge-to-edge full-screen so question
          transitions don't shrink/grow the dialog. On ≥sm we keep the
          rounded centred card with a 90vh cap. The flex column lets the
          header stay sticky and the body scroll independently.       */}
      <div
        className={`relative w-full sm:max-w-2xl flex flex-col sm:mx-4 sm:rounded-[16px] overflow-hidden h-[100dvh] sm:h-auto sm:max-h-[90vh] sm:min-h-[420px] ${isDark ? 'bg-gray-900' : 'bg-white'}`}
        data-testid="quiz-modal"
      >

        {/* Header bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 bg-[#0d4a8a] text-white">
          <span className="text-[14px] font-medium truncate">{quiz.title}</span>
          <button onClick={() => handleClose(result ? { playerName, score: result.score } : null)} className="text-white/70 text-[22px] leading-none">&times;</button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {/* Phase: Name Entry */}
          {phase === 'name' && (
            <div className="space-y-5 text-center py-4">
              <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center text-[28px] font-bold text-white bg-brand-500`}>?</div>
              <div>
                <p className={`text-[18px] font-medium ${textMain}`}>{quiz.title}</p>
                <p className={`text-[13px] ${textMuted} mt-1.5`}>{questions.length} questions · {timePerQ > 0 ? `${timePerQ}s per question` : 'No time limit'} · No sign-in required</p>
              </div>
              <input
                value={playerName}
                onChange={e => setPlayerName(e.target.value)}
                placeholder="Enter your name to start"
                className={`w-full max-w-sm mx-auto block text-[15px] text-center px-5 py-3.5 rounded-[12px] border focus:outline-none focus:ring-2 focus:ring-brand-500/50 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}
                data-testid="guest-name-input"
                onKeyDown={e => e.key === 'Enter' && handleStart()}
                autoFocus
              />
              <Button onClick={handleStart} className="w-full max-w-sm mx-auto block bg-brand-500 hover:bg-brand-600 text-white rounded-[12px] py-3 text-[15px] font-medium" data-testid="start-guest-quiz-btn">
                Start Quiz
              </Button>
            </div>
          )}

          {/* Phase: Checking name */}
          {phase === 'checking' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className={`text-[13px] ${textMuted}`}>Checking name...</p>
            </div>
          )}

          {/* Phase: Countdown */}
          {phase === 'countdown' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-[72px] font-bold text-brand-500 animate-bounce">{countdownNum || 'Go!'}</div>
              <p className={`text-[14px] ${textMuted} mt-3`}>Get ready, {playerName}!</p>
            </div>
          )}

          {/* Phase: Playing */}
          {phase === 'playing' && (() => {
            const q = questions[currentQ];
            if (!q) return null;
            const selected = answers[q.question_id];
            return (
              <div>
                {/* Timer + progress */}
                <div className="flex items-center gap-3 mb-4">
                  <span className={`text-[12px] font-medium ${textMuted}`}>Q{currentQ + 1}/{questions.length}</span>
                  <div className={`flex-1 h-1.5 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <div className="h-full rounded-full bg-brand-500" style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }} />
                  </div>
                  {timePerQ > 0 && (
                    <div className="flex items-center gap-1.5">
                      <svg width="20" height="20" viewBox="0 0 36 36" className="flex-shrink-0">
                        <circle cx="18" cy="18" r="16" fill="none" stroke={isDark ? '#374151' : '#e5e7eb'} strokeWidth="3" />
                        <circle cx="18" cy="18" r="16" fill="none" stroke={timerColor} strokeWidth="3"
                          strokeDasharray={`${timerPct} ${100 - timerPct}`} strokeDashoffset="25" strokeLinecap="round"
                          style={{ transition: 'stroke-dasharray 0.3s, stroke 0.3s' }} />
                      </svg>
                      <span className="text-[13px] font-medium" style={{ color: timerColor }}>{timer}s</span>
                    </div>
                  )}
                </div>

                {/* Question */}
                {q.image_url && (
                  // Render the question's image when present. We cap it at
                  // 240px tall so it never elbows the options off-screen on
                  // shorter viewports; the `object-contain` keeps the
                  // aspect ratio so creators can use any source dimension.
                  <div className="mb-3 flex items-center justify-center">
                    <img
                      src={q.image_url}
                      alt=""
                      loading="lazy"
                      className="max-h-[240px] w-auto max-w-full rounded-[10px] border border-gray-100"
                      data-testid="quiz-question-image"
                    />
                  </div>
                )}
                <p className={`text-[16px] font-medium mb-5 ${textMain}`}>{q.text}</p>

                {/* Options */}
                <div className="space-y-2.5">
                  {(q.options || []).map((opt, oi) => {
                    const isSelected = selected === oi;
                    const justPicked = selectedAnim === oi;
                    let optStyle = isDark ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-[#0e1f35]';
                    if (isSelected && !showCorrect) optStyle = isDark ? 'border-brand-500 bg-brand-900/40 text-brand-300' : 'border-[#0d4a8a] bg-[#e8f0f9] text-[#082f5a]';
                    if (showCorrect && justPicked) optStyle = 'border-emerald-500 bg-emerald-50 text-emerald-700';

                    return (
                      <button
                        key={oi}
                        onClick={() => selectAnswer(oi)}
                        disabled={selectedAnim !== null}
                        className={`w-full text-left px-4 py-3 rounded-[12px] text-[14px] border ${optStyle} ${justPicked ? 'scale-[0.98]' : ''}`}
                        style={{ transition: 'all 0.15s' }}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold ${isSelected ? 'bg-brand-500 text-white' : isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                            {String.fromCharCode(65 + oi)}
                          </span>
                          {/* Option image — small thumb so options stay
                              compact even when each carries an illustration. */}
                          {opt.image_url && (
                            <img
                              src={opt.image_url}
                              alt=""
                              loading="lazy"
                              className="flex-shrink-0 w-14 h-14 rounded-[8px] object-cover border border-gray-100"
                              data-testid={`quiz-option-${oi}-image`}
                            />
                          )}
                          <span className="flex-1 break-words">{opt.text}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Phase: Submitting */}
          {phase === 'submitting' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-brand-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className={`text-[13px] ${textMuted}`}>Calculating results...</p>
            </div>
          )}

          {/* Phase: Results */}
          {phase === 'results' && result && (
            <div className="space-y-4">
              {/* Score circle */}
              <div className="text-center py-2">
                <div className="relative w-24 h-24 mx-auto mb-3">
                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke={isDark ? '#374151' : '#e5e7eb'} strokeWidth="2.5" />
                    <circle cx="18" cy="18" r="15.9" fill="none"
                      stroke={result.passed ? '#10B981' : '#EF4444'} strokeWidth="2.5"
                      strokeDasharray={`${result.score} ${100 - result.score}`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-[22px] font-medium ${textMain}`}>{result.score}%</span>
                  </div>
                </div>
                <p className={`text-[15px] font-medium ${result.passed ? 'text-emerald-500' : 'text-red-500'}`}>
                  {result.passed ? 'You passed!' : 'Keep practicing!'}
                </p>
                <p className={`text-[12px] ${textMuted}`}>{result.correct}/{result.total} correct · Pass mark {result.pass_mark}%</p>
              </div>

              {/* Leaderboard */}
              {result.leaderboard && result.leaderboard.length > 0 && quiz?.leaderboard_enabled !== false && (
                <div>
                  <p className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${textMuted}`}>Leaderboard</p>
                  <div className={`rounded-[10px] border ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    {result.leaderboard.slice(0, 5).map((p, i) => {
                      const isYou = p.name === playerName;
                      return (
                        <div key={i} className={`flex items-center gap-2.5 px-3 py-2 ${i < Math.min(result.leaderboard.length, 5) - 1 ? `border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}` : ''} ${isYou ? (isDark ? 'bg-brand-900/20' : 'bg-brand-50/50') : ''}`}>
                          <span className="w-4 text-[12px] font-semibold text-center" style={{ color: i < 3 ? RANK_COLORS[i] : (isDark ? '#6b7280' : '#9ca3af') }}>
                            {i + 1}
                          </span>
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold text-white flex-shrink-0" style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                            {(p.name || '?').substring(0, 2).toUpperCase()}
                          </div>
                          <span className={`text-[12px] flex-1 truncate ${isYou ? 'font-medium' : ''} ${textMain}`}>
                            {p.name}{isYou ? ' (you)' : ''}
                          </span>
                          <span className={`text-[12px] font-medium ${textMuted}`}>{p.score}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Review answers */}
              {result.results && result.results.length > 0 && (
                <details className="group">
                  <summary className={`text-[12px] font-medium cursor-pointer ${textMuted}`}>Review answers</summary>
                  <div className="space-y-2 mt-2">
                    {result.results.map((r, i) => (
                      <div key={i} className={`p-2.5 rounded-[8px] text-[12px] ${r.is_correct ? (isDark ? 'bg-emerald-900/20 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200') : (isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200')}`}>
                        <p className={`font-medium ${textMain}`}>Q{i + 1}. {r.text}</p>
                        <p className={r.is_correct ? 'text-emerald-600 mt-0.5' : 'text-red-500 mt-0.5'}>
                          {r.is_correct ? '✓ Correct' : `✗ Answer: ${r.options?.[r.correct_answer]?.text || ''}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button onClick={() => { setCurrentQ(0); setAnswers({}); setResult(null); setPhase('countdown'); setCountdownNum(3); }}
                  variant="ghost" className={`flex-1 rounded-[12px] text-[14px] ${textMuted}`}>
                  Play again
                </Button>
                <Button onClick={() => onClose(result ? { playerName, score: result.score } : null)} className="flex-1 bg-brand-500 hover:bg-brand-600 text-white rounded-[12px] text-[14px] font-medium">
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
